<template>
  <div class="vertical-center-dialog">
    <el-dialog
      title="计划内容"
      :visible.sync="dialogVisible"
      width="50%"
      :close-on-click-modal="false"
      :append-to-body="false"
      :modal-append-to-body="false"
      @close="handleClose"
      custom-class="plan-dialog-width-limit">
      <div class="plan-info markdown-body" v-html="renderedHtml"></div>
      
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleClose">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'PlanInfoDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    planInfo: {
      type: String,
      default: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    renderedHtml() {
      const src = this.planInfo || ''

      // 暂时禁用预处理，直接使用原始内容
      // const processedSrc = this.preprocessTableFormat(src)

      // 自定义链接渲染：新窗口打开
      const renderer = new marked.Renderer()
      renderer.link = function(href, title, text) {
        const t = title ? ` title="${title}"` : ''
        return `<a href="${href}" target="_blank" rel="noopener noreferrer"${t}>${text}</a>`
      }

      // 自定义表格渲染：确保表格结构完整
      renderer.table = function(header, body) {
        return `<table class="markdown-table">\n<thead>\n${header}</thead>\n<tbody>\n${body}</tbody>\n</table>`
      }

      // 任务列表渲染：为包含复选框的列表增加类名
      renderer.list = function(body, ordered, start) {
        const hasTaskItems = body.includes('<input type="checkbox"')
        const listType = ordered ? 'ol' : 'ul'
        const startAttr = ordered && start !== 1 ? ` start="${start}"` : ''
        if (hasTaskItems) {
          return `<${listType} class="task-list"${startAttr}>\n${body}</${listType}>\n`
        }
        return `<${listType}${startAttr}>\n${body}</${listType}>\n`
      }
      renderer.listitem = function(text) {
        if (text.includes('<input type="checkbox"')) {
          return `<li class="task-list-item">${text}</li>\n`
        }
        return `<li>${text}</li>\n`
      }

      // 将 - [ ] / - [x] 预处理为带有禁用复选框的列表项
      const originalLex = marked.Lexer.lex
      marked.Lexer.lex = function(src, options) {
        // 同时处理 "- [ ]" "* [ ]" "+ [ ]" 三种无序前缀
        src = src.replace(/^(?:-|\*|\+) \[([ x])\] (.+)$/gm, function(_m, checked, text) {
          const attr = checked === 'x' ? 'checked' : ''
          return `- <input type="checkbox" ${attr} disabled> ${text}`
        })
        return originalLex.call(this, src, options)
      }

      const html = marked(src, { gfm: true, breaks: true, renderer })

      // 渲染 KaTeX 数学公式（支持 $$block$$ 与 $inline$）
      const withBlockMath = html.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => {
        try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch(e) { return _ }
      })
      const withInlineMath = withBlockMath.replace(/(^|[^$])\$([^$]+?)\$([^$]|$)/g, (m, pre, expr, post) => {
        try { return pre + katex.renderToString(expr, { displayMode: false, throwOnError: false }) + post } catch(e) { return m }
      })

      // 配置DOMPurify允许表格相关的标签和属性
      const cleanHtml = DOMPurify.sanitize(withInlineMath, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'input',
          'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption'
        ],
        ALLOWED_ATTR: [
          'href', 'target', 'rel', 'title', 'class', 'id', 'src', 'alt',
          'type', 'disabled', 'checked'
        ],
        KEEP_CONTENT: true
      })

      return cleanHtml
    }
  },
  methods: {
    // 预处理表格格式：将非标准的表格格式转换为标准markdown表格
    preprocessTableFormat(content) {
      if (!content) return content
      
      const lines = content.split('\n')
      const processedLines = []
      let inTable = false
      let tableLines = []
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        
        // 检查是否是分隔行（只包含 --- 和空格）
        const isSeparatorLine = /^[\s\-|]+$/.test(line) && line.includes('---')
        
        // 检查是否是表格行（包含 | 分隔符或者看起来像表格的格式）
        const hasPipeSeparators = line.includes('|') && line.split('|').length >= 3
        const looksLikeTableRow = !hasPipeSeparators && !isSeparatorLine && 
                                 (line.includes('序号') || /^\d+\s+[\u4e00-\u9fa5]+\s+[\d\.\-\s~]+/.test(line))
        
        if (hasPipeSeparators || looksLikeTableRow) {
          if (!inTable) {
            inTable = true
            tableLines = []
          }
          tableLines.push(line)
        } else if (isSeparatorLine) {
          // 如果是分隔行，且之前有表格行，则添加到表格中
          if (inTable && tableLines.length > 0) {
            tableLines.push(line)
          } else {
            // 如果之前没有表格行，则作为普通文本处理
            processedLines.push(lines[i])
          }
        } else {
          // 如果之前有表格，先处理表格
          if (inTable && tableLines.length > 0) {
            processedLines.push(...this.convertToMarkdownTable(tableLines))
            tableLines = []
            inTable = false
          }
          processedLines.push(lines[i])
        }
      }
      
      // 处理最后的表格
      if (inTable && tableLines.length > 0) {
        processedLines.push(...this.convertToMarkdownTable(tableLines))
      }
      
      return processedLines.join('\n')
    },
    
    // 将表格行转换为标准markdown表格格式
    convertToMarkdownTable(tableLines) {
      if (tableLines.length === 0) return []
      
      const result = []
      let hasSeparator = false
      
      // 检查是否已经有分隔行
      for (const line of tableLines) {
        if (/^[\s\-|]+$/.test(line) && line.includes('---')) {
          hasSeparator = true
          break
        }
      }
      
      // 处理每一行
      for (let i = 0; i < tableLines.length; i++) {
        const line = tableLines[i]
        
        // 如果是分隔行，跳过（避免重复）
        if (/^[\s\-|]+$/.test(line) && line.includes('---')) {
          continue
        }
        
        // 将行转换为标准markdown表格格式
        const convertedLine = this.convertLineToMarkdownTable(line)
        result.push(convertedLine)
        
        // 如果是第一行（表头），且没有分隔行，则添加分隔行
        if (i === 0 && !hasSeparator) {
          const columns = convertedLine.split('|').length - 2 // 减去开头和结尾的空字符串
          const separator = '|' + '---|'.repeat(columns - 1)
          result.push(separator)
        }
      }
      
      return result
    },
    
    // 将单行转换为markdown表格格式
    convertLineToMarkdownTable(line) {
      // 如果已经有 | 分隔符，直接返回
      if (line.includes('|')) {
        return line
      }
      
      // 将空格分隔的内容转换为 | 分隔的格式
      const parts = line.split(/\s+/).filter(part => part.trim() !== '')
      return '|' + parts.join('|') + '|'
    },
    
    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.plan-info {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.6;
  color: #2c3e50;
}

.markdown-body {
  h1, h2, h3, h4, h5, h6 { margin: 12px 0 8px; font-weight: 600; }
  h1 { font-size: 22px; }
  h2 { font-size: 20px; }
  h3 { font-size: 18px; }
  p { margin: 8px 0; }
  ul, ol { padding-left: 25px !important; margin: 8px 0; }
  li { margin: 4px 0; }

  /* 任务列表样式（弹窗内） */
  ul.task-list {
    list-style: none !important;
    padding-left: 10px !important;
    margin: 8px 0;
  }
  ul.task-list li.task-list-item {
    list-style: none !important;
    display: flex;
    align-items: center;
  }
  ul.task-list li.task-list-item::marker { content: '' !important; }
  ul.task-list li.task-list-item::before { display: none !important; }
  ul.task-list li.task-list-item input[type="checkbox"] {
    margin: 0 8px 0 0;
    flex-shrink: 0;
    transform: scale(1.2);
    vertical-align: middle;
    position: relative;
    top: -1px;
  }

  /* 兜底：凡是包含checkbox的ul都当作任务列表处理 */
  ul:has(li input[type="checkbox"]) {
    list-style: none !important;
    padding-left: 10px !important;
  }
  ul:has(li input[type="checkbox"]) li::marker { content: '' !important; }
  
  /* 再次增强选择器权重，彻底移除小黑点（scoped 下用 :deep 渗透到 v-html） */
  :deep(.plan-info ul.task-list),
  :deep(.plan-info.markdown-body ul.task-list),
  :deep(.plan-info.markdown-body ul:has(li input[type="checkbox"])) {
    list-style: none !important;
    padding-left: 10px !important;
  }
  :deep(.plan-info.markdown-body ul li.task-list-item),
  :deep(.plan-info.markdown-body ul li:has(input[type="checkbox"])) {
    list-style: none !important;
    display: flex;
    align-items: center;
  }
  :deep(.plan-info.markdown-body ul li.task-list-item::before),
  :deep(.plan-info.markdown-body ul li:has(input[type="checkbox"])::before) { display: none !important; }
  :deep(.plan-info.markdown-body ul li.task-list-item::marker),
  :deep(.plan-info.markdown-body ul li:has(input[type="checkbox"])::marker) { content: '' !important; }

  /* 有主题通过 p::before 绘制圆点，任务列表需额外移除 */
  :deep(.plan-info.markdown-body ul li.task-list-item > p::before),
  :deep(.plan-info.markdown-body ul li:has(input[type="checkbox"]) > p::before) {
    content: none !important;
    display: none !important;
  }

  /* 统一有序/无序/任务列表的行间距 */
  :deep(.plan-info.markdown-body ul > li),
  :deep(.plan-info.markdown-body ol > li) {
    margin-top: 5px !important;
    margin-bottom: 5px !important;
  }
  /* 列表项里的段落默认有 margin，重置为 0，避免撑开行距 */
  :deep(.plan-info.markdown-body ul > li > p),
  :deep(.plan-info.markdown-body ol > li > p) {
    margin: 0 !important;
  }

  /* 兜底：任何任务项都强制没有 marker */
  :deep(.plan-info.markdown-body ul li.task-list-item) {
    list-style: none !important;
  }
  :deep(.plan-info.markdown-body ul li.task-list-item::marker) {
    content: '' !important;
  }
  
  /* 增强表格样式 - 确保PC端正确渲染 */
  table { 
    border-collapse: collapse !important; 
    width: 100% !important; 
    margin: 8px 0 !important; 
    display: table !important;
    border-spacing: 0 !important;
    border: 1px solid #e5e7eb !important;
    table-layout: auto !important;
  }
  thead { display: table-header-group !important; }
  tbody { display: table-row-group !important; }
  tr { display: table-row !important; }
  th, td { 
    border: 1px solid #e5e7eb !important; 
    padding: 8px 12px !important; 
    text-align: left !important; 
    display: table-cell !important;
    vertical-align: top !important;
    word-wrap: break-word !important;
  }
  thead th { 
    background: #f3f4f6 !important; 
    font-weight: 600 !important; 
    border-bottom: 2px solid #e5e7eb !important;
  }
  tbody tr:nth-child(even) { background-color: #f9fafb !important; }
  tbody tr:hover { background-color: #f3f4f6 !important; }
  
  /* 专门为markdown-table类添加样式 */
  .markdown-table {
    border-collapse: collapse !important;
    width: 100% !important;
    margin: 8px 0 !important;
    display: table !important;
    border-spacing: 0 !important;
    border: 1px solid #e5e7eb !important;
  }
  .markdown-table thead { display: table-header-group !important; }
  .markdown-table tbody { display: table-row-group !important; }
  .markdown-table tr { display: table-row !important; }
  .markdown-table th, .markdown-table td { 
    border: 1px solid #e5e7eb !important; 
    padding: 8px 12px !important; 
    text-align: left !important; 
    display: table-cell !important;
    vertical-align: top !important;
  }
  .markdown-table thead th { 
    background: #f3f4f6 !important; 
    font-weight: 600 !important; 
    border-bottom: 2px solid #e5e7eb !important;
  }
  .markdown-table tbody tr:nth-child(even) { background-color: #f9fafb !important; }
  .markdown-table tbody tr:hover { background-color: #f3f4f6 !important; }
  
  code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
  pre { background: #1f2937; color: #e5e7eb; padding: 10px 12px; border-radius: 6px; overflow: auto; }
  blockquote { margin: 8px 0; padding: 6px 10px; border-left: 4px solid #dcdfe6; background: #ffffff; color: #606266; }
  a { color: #409EFF; text-decoration: none; }
  a:hover { text-decoration: underline; }
}

.dialog-footer {
  text-align: right;
}
</style>

<!-- 全局样式：确保PC端表格正确渲染 -->
<style>
/* 列表统一行距（全局，提升优先级，作用于 v-html 渲染内容） */
.plan-info.markdown-body ul > li,
.plan-info.markdown-body ol > li,
.plan-info.markdown-body ul > li > p,
.plan-info.markdown-body ol > li > p {
  margin-top: 5px !important;
  margin-bottom: 5px !important;
}
/* 段落重置，避免撑开 */
.plan-info.markdown-body ul > li > p,
.plan-info.markdown-body ol > li > p {
  margin: 0 !important;
}

/* 强制移除任务列表的 marker（覆盖主题/浏览器默认） */
.plan-info.markdown-body ul.task-list,
.plan-info.markdown-body ul.task-list > li.task-list-item {
  list-style: none !important;
  list-style-type: none !important;
}
.plan-info.markdown-body ul.task-list > li.task-list-item::marker {
  content: '' !important;
}

/* 统一左侧缩进（高优先级作用于 v-html 内容） */
.plan-info.markdown-body ul,
.plan-info.markdown-body ol {
  padding-left: 25px !important;
}
.plan-info.markdown-body ul.task-list,
.plan-info.markdown-body ul:has(li input[type="checkbox"]) {
  padding-left: 10px !important;
}

/* 去除任务列表下的二级无序列表小点 */
.plan-info.markdown-body ul.task-list ul,
.plan-info.markdown-body ul:has(li input[type="checkbox"]) ul {
  list-style: none !important;
}
.plan-info.markdown-body ul.task-list ul > li::marker,
.plan-info.markdown-body ul:has(li input[type="checkbox"]) ul > li::marker {
  content: '' !important;
}
.plan-info.markdown-body ul.task-list ul > li > p::before,
.plan-info.markdown-body ul:has(li input[type="checkbox"]) ul > li > p::before {
  content: none !important;
  display: none !important;
}
/* 全局表格样式 - 确保PC端markdown表格正确渲染 */
.plan-info table,
.markdown-body table,
.markdown-table {
  border-collapse: collapse !important;
  width: 100% !important;
  margin: 8px 0 !important;
  display: table !important;
  border-spacing: 0 !important;
  border: 1px solid #e5e7eb !important;
  table-layout: auto !important;
}

.plan-info table thead,
.markdown-body table thead,
.markdown-table thead {
  display: table-header-group !important;
}

.plan-info table tbody,
.markdown-body table tbody,
.markdown-table tbody {
  display: table-row-group !important;
}

.plan-info table tr,
.markdown-body table tr,
.markdown-table tr {
  display: table-row !important;
}

.plan-info table th,
.plan-info table td,
.markdown-body table th,
.markdown-body table td,
.markdown-table th,
.markdown-table td {
  border: 1px solid #e5e7eb !important;
  padding: 8px 12px !important;
  text-align: left !important;
  display: table-cell !important;
  vertical-align: top !important;
  word-wrap: break-word !important;
}

.plan-info table thead th,
.markdown-body table thead th,
.markdown-table thead th {
  background: #f3f4f6 !important;
  font-weight: 600 !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.plan-info table tbody tr:nth-child(even),
.markdown-body table tbody tr:nth-child(even),
.markdown-table tbody tr:nth-child(even) {
  background-color: #f9fafb !important;
}

.plan-info table tbody tr:hover,
.markdown-body table tbody tr:hover,
.markdown-table tbody tr:hover {
  background-color: #f3f4f6 !important;
}
</style>

<style lang="scss">
// 全局样式 - 限制对话框最大宽度
.el-dialog__wrapper .plan-dialog-width-limit {
  width: 50% !important;
  max-width: 800px !important;
  margin: 0 auto !important;
}

.plan-dialog-width-limit.el-dialog {
  width: 100% !important;
}
</style>
 